/**
 * components/workspace/AnalyseFitSection.jsx — RC 1.49.7 Phase 3
 *
 * Live Analyse Fit section for the route-based Application
 * Workspace. Mirrors the wizard's `runAnalyseFitOnSelected` +
 * P0-FIN-LIFECYCLE-4 contract verbatim — same endpoint, same
 * `runAnalyzeFitWithPolling` helper, same in-flight recovery
 * marker, same three-control footer.
 *
 * Hard rules (operator-locked, RC 1.49.x + RC 1.49.7):
 *
 *   • RC 1.49.x.RUN_ISOLATION: NEVER auto-redispatch
 *     `/analyze-fit` simply because the page remounted. Mount-
 *     time hydration uses GET `/best-fit` ONLY (read-only, zero
 *     charge). The user's explicit click is the only path that
 *     fires the chargeable POST.
 *
 *   • RC 1.49.7 Phase 3 belt-and-braces job + source-scoping
 *     (operator 2026-02): any `/best-fit` row surfaced here MUST
 *     match
 *       (a) the user's marker `(user_id, job_id)` — implicit
 *           from the marker storage key
 *       (b) `row.source_type === backendSourceType` from URL
 *       (c) `row.source_id === sourceCvId` from URL
 *     Recovery additionally guards `evaluated_at >=
 *     marker.submittedAtMs` to disambiguate a row newly persisted
 *     by THIS run from any older cached row for the same source.
 *
 *   • RC 1.48.1 async polling: chargeable POST is wrapped via
 *     `runAnalyzeFitWithPolling(jobId, body, { signal,
 *     onRecoveryState })`. Operator-locked toast copy preserved
 *     verbatim.
 *
 *   • RC 1.49.5 L-3 inflight-fit marker: `writeInflightFitMarker`
 *     fires BEFORE the chargeable POST so a tab close mid-run is
 *     recoverable on next mount via read-only `/best-fit` poll.
 *     The marker is sessionStorage-only and never charges credits.
 *
 *   • RC 1.49.5A P0-A3: chargeable button hard-disabled while
 *     `creditsLoading`.
 *
 *   • RC 1.49.7 Phase 2 workspace UI rule: no floating panels
 *     (no `<Sheet>`, `<Dialog>`, `<Drawer>`, `<Popover>`). ONE
 *     primary action button per section. All options inline.
 *
 *   • Run ≠ Continue — three separate controls. Continue is
 *     DISABLED while `running || recoveryActive` (operator #9 +
 *     #10) and ENABLED otherwise (operator #11 — skip allowed).
 *
 *   • Pricing label: ALWAYS sourced from
 *     `getActionCost(usageCosts, 'analyze_fit')`, never hard-coded.
 *
 *   • Force-reanalyse stays EXPLICIT (operator #8). The standard
 *     run never sends `force_override: true`. Backend's existing
 *     409 INFERIOR_SOURCE / INFERIOR_VARIANT contract surfaces
 *     when applicable. Inline force-confirmation flow deferred to
 *     a Phase 3 polish (no modal allowed by workspace UI rule).
 *
 *   • URL-tied recovery (operator #3): the marker key is
 *     `inflight-fit:${userId}:${jobId}` — `jobId` is read from
 *     `useParams()` which is URL-derived, so the same URL on a
 *     refresh / tab restore re-binds the same marker.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Sparkles, Loader2, Target, CheckCircle2, Info,
  AlertTriangle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useCredits, getActionCost,
} from '@/lib/creditsContext';
import { handleCreditsError } from '@/lib/handleCreditsError';
import { generateInterviewPrepWithPolling, pollInterviewPrepUntilTerminal } from '@/lib/interviewPrepPolling';
import { apiFetch } from '@/lib/api';

// RC 1.53 Phase 4.3A — controller wiring.
// Interview Prep is now dispatched through the OperationLifecycle-
// Controller. The actual polling helper is unchanged (the adapter
// forwards the call verbatim); the controller gives us STABILIZING
// status, workspace-wide Generate-lock aggregation, and the visible
// activity indicator that survives tab switching.
import {
  dispatch as controllerDispatch,
  emit as controllerEmit,
} from '@/lib/operationController';
import { useOperationLifecycle } from '@/lib/operationController/useOperationLifecycle';
import { OPERATION_EVENT_TYPES } from '@/lib/operationController/operationEvents';
import * as interviewPrepAdapter from '@/lib/operationController/adapters/interviewPrepAdapter';
import { trace as actionTrace } from '@/lib/operationController/actionTrace';
import { verifyAndHydrateInterviewPrep } from '@/lib/operationController/postTerminalHydrator';
import { useArtefactRehydrate } from '@/lib/operationController/useArtefactRehydrate';
// Phase -1 Tier 1 — S5b (operator-locked, 2026-02-15) — backend-
// authoritative mount reconcile against `/api/operations/me/active`.
// Replaces the SS-marker recovery path (E2) as the canonical source
// of truth for "is there an in-flight Interview Prep run for this
// (job, action)?". E2 stays in place as a fallback for environments
// where the 075 migration hasn't been applied yet (graceful
// degradation by design — see useOperationMountReconcile.js).
import { useOperationMountReconcile } from '@/lib/operationController/useOperationMountReconcile';
import WorkspaceActivityIndicator from '@/components/workspace/WorkspaceActivityIndicator';
// ── Bundle U-9.G — Loaded-from-history chip (2026-02-15) ────────
import HistoryBadge from '@/components/workspace/HistoryBadge';
import {
  getOrMintIdempotencyKey, clearIdempotencyKey,
} from '@/lib/idempotencyKeys';
import {
  writeInflightInterviewPrepMarker, patchInflightInterviewPrepRunId,
  readInflightInterviewPrepMarker, clearInflightInterviewPrepMarker,
  isInflightInterviewPrepMarkerExpired,
} from '@/lib/inflightInterviewPrepRecovery';
// RC 1.49.7 Slice 6b — Interview Prep success invalidates the same
// list caches the wizard does so JobsPage / DashboardPage refetch.
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import {
  mapInterviewPrep,
} from '@/lib/interviewPrepShape';
import InterviewPrepViewer from '@/components/interview/InterviewPrepViewer';
import {
  markInterviewPrepUnlocked,
  getInterviewPrepResult,
} from '@/lib/interviewPrepUnlock';
// RC 1.52 Phase 3B.1 — UX cooldown for Interview Prep CTA so the
// user can't double-fire on a stale wallet pill.
import {
  startCooldown,
  interviewPrepCooldownKey,
  useCooldown,
} from '@/lib/actionCooldown';

// Mount-time recovery poll cadence — mirrors wizard L2036-2057.
const RECOVERY_INTERVAL_MS = 8000;
const RECOVERY_MAX_ATTEMPTS = 10;

// RC 1.51 (operator-locked, 2026-02) — Workspace Interview Prep is
// blind to baseline analyse_fit hydration. Helper kept for parity
// with the legacy shape so the result-card render path is
// unchanged. ONLY consumed by:
//   • the in-process post-run setResult (handleRun)
//   • the localStorage cache rehydrate (read-only, click-gated by
//     `isInterviewPrepUnlocked` — never auto-binds baseline data)
function _resultFromInterviewPrepPayload(payload, ctx) {
  if (!payload) return null;
  return {
    score: payload.score || null,
    analysis: null,
    interview_prep: payload.interview_prep || null,
    billing: { ...(payload.billing || {}), cached: true },
    sourceType: ctx.sourceType,
    sourceId:   ctx.sourceId,
  };
}

export default function InterviewPrepSection({
  job,
  jobId,
  primaryCv,
  onContinue,
  onBack,
  onBusyChange,
  // RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
  workspaceBusy = false,
  // ── Bundle U-9.B props (2026-02-15) ─────────────────────────────
  lookupStatus = 'ready',
  loadedFromHistory = false,
  rootTestId = 'workspace-fit-section',
}) {
  const credits = useCredits();
  const usageCosts = credits?.usageCosts;
  const creditsLoading = !credits || !!credits.loading;

  const fitCost = getActionCost(usageCosts, 'interview_prep');
  const fitCostLabel = fitCost == null
    ? null
    : `${fitCost} ${fitCost === 1 ? 'Credit' : 'Credits'}`;

  // ── CV carry-forward (READ-ONLY here) ────────────────────────────
  // CvSection writes `?source_cv_id` + `?source_cv_type`.
  // Falls back to primaryCv when params absent (skip-CV path).
  //
  // RC 1.49.7 Slice 5 (operator P0, 2026-02): we ALSO write the
  // analyse-fit URL triple (`analyse_fit_id`, `analyse_fit_source_type`,
  // `analyse_fit_source_id`) on every successful binding so PackSection
  // can read deterministic state across navigation instead of racing
  // `/best-fit` after a fresh run. RC 1.51 — setSearchParams no
  // longer destructured (the legacy URL-triple writer was removed).
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceCvType = searchParams.get('source_cv_type') || 'base_cv';
  const sourceCvId = searchParams.get('source_cv_id') || primaryCv?.id || null;
  // Translate to the backend's source_type vocabulary.
  const backendSourceType = sourceCvType === 'cv_variant' ? 'cv_variant' : 'uploaded_cv';

  // ── Result + run state ───────────────────────────────────────────
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  // RC 1.52 Phase 3B.1 — sourceId-scoped Interview Prep cooldown.
  // Starts on confirmed successful dispatch in `handleRun`.
  // Different (job, source) pairs have independent cooldowns so
  // generating prep on a new variant after the Source CV doesn't
  // get blocked by the prior cooldown.
  const interviewPrepCooldown = useCooldown(interviewPrepCooldownKey(
    primaryCv?.user_id, jobId, sourceCvId,
  ));
  // RC 1.50 Workspace UX Slice 0 — emit busy state to parent.
  useEffect(() => {
    onBusyChange?.(running);
  }, [running, onBusyChange]);

  // RC 1.53 Phase 4.3A — controller lifecycle subscription.
  // Drives the activity indicator + Generate-button gating across
  // `dispatching → polling → stabilizing → complete`. Section's own
  // `running` boolean still owns its local UI logic.
  const {
    status: controllerStatus,
    markComplete: lifecycleMarkComplete,
    markFailed:   lifecycleMarkFailed,
  } = useOperationLifecycle(jobId, 'interview_prep');
  // RC 1.53 Phase 4.3A — progress_label auxiliary state for the
  // activity indicator.
  const [progressLabel, setProgressLabel] = useState(null);
  // RC 1.51 (operator-locked, 2026-02) — `restoring` is now bound
  // by a SINGLE synchronous localStorage read on mount, so the
  // banner appears + clears in one render. No multi-effect counter,
  // no /best-fit polling, no inflight-marker recovery — Interview
  // Prep is blind to baseline analyse_fit hydration.
  const [restoring, setRestoring] = useState(true);
  // Phase -1 Tier 1 — S7 (operator-locked, 2026-02-15) — separate
  // "recovering" state for the read-only in-flight re-attach poll.
  // Distinct from `running` so the Generate button STAYS ENABLED
  // while we silently re-attach to a backend run that survived a
  // refresh / section-switch. Closes the
  // OPERATION_LIFECYCLE_AUDIT.md Part 1 cause 1 (mount-trigger
  // execution perception). The user can still click Generate while
  // a recovery poll is in flight — `handleRun` will short-circuit
  // via the orchestrator's idempotency replay if the run is
  // actually still in progress, or re-dispatch fresh otherwise.
  const [recovering, setRecovering] = useState(false);

  // Phase -1 Tier 1 — S5b — backend mount-reconcile against the
  // unified `operations` state-of-record. Drives "safer recovery":
  //   • When the backend confirms an in-flight op exists → we let
  //     E2 proceed as today (idempotent — SS marker should also be
  //     present; if not, we use the BE-supplied runId).
  //   • When the backend confirms no op exists (or a terminal one)
  //     → any stale SS marker is cleared so E2 cannot start a stale
  //     recovery loop.
  //   • When the backend is unavailable (migration not applied /
  //     503) → the hook reports `'error'` and E2's existing
  //     SS-marker fallback takes over unchanged.
  // The hook itself is purely observational — it does NOT dispatch,
  // it does NOT poll the run, it does NOT touch the wallet. It just
  // reads `/api/operations/me/active`.
  const reconcile = useOperationMountReconcile({
    targetKind: 'job',
    targetId:   jobId,
    actionCode: 'interview_prep',
    enabled:    !!(jobId && primaryCv?.user_id),
    apiFetch:   (path, opts) => apiFetch(path, opts),
    onTerminal: (row) => {
      // BE says this job's interview_prep is terminal. If the URL
      // doesn't already carry the canonical id, hint it so E3
      // (URL-bound rehydrate) picks up the canonical row on its
      // next pass. Any stale SS marker is also cleared — the run
      // is done; no recovery needed.
      try {
        if (row?.result_envelope_ref && !searchParams.get('interview_prep_id')) {
          const _next = new URLSearchParams(searchParams);
          _next.set('interview_prep_id', row.result_envelope_ref);
          if (typeof setSearchParams === 'function') {
            setSearchParams(_next, { replace: true });
          }
        }
      } catch { /* noop */ }
      try {
        clearInflightInterviewPrepMarker(primaryCv?.user_id, jobId);
      } catch { /* noop */ }
    },
    onNone: () => {
      // BE confirms no in-flight op for this (job, interview_prep).
      // Any SS marker we hold is stale — clear it so E2 does not
      // start a recovery poll on a non-existent run.
      try {
        clearInflightInterviewPrepMarker(primaryCv?.user_id, jobId);
      } catch { /* noop */ }
    },
    // U-2.IP (2026-02-15) — `onRecovering` wired. Surfaces the
    // RECOVERING UX while the FE waits for either:
    //   (a) the in-flight tail to complete (then E2's poll loop or
    //       this hook's `onTerminal` will bind the result), or
    //   (b) the by-job canonical fallback below to land an existing
    //       terminal artefact.
    // Read-only — never disables Generate, never charges.
    onRecovering: () => {
      try { setRecovering(true); } catch { /* noop */ }
    },
    // onError intentionally not wired — E2's existing SS-marker
    // path handles BE unavailability.
  });
  // RC 1.49.7 H2 (operator 2026-02): inline inferior-source /
  // inferior-variant 409 confirmation. Set when the backend's
  // R2/R3 reject-gate (jobs.py:1721) fires. The user then has a
  // calm inline choice — "Keep existing analysis" (clear state,
  // no charge, no new run) OR "Analyse anyway" (retry the POST
  // with `force_override: true`). This is the ONLY path that may
  // bypass the inferior-source guard.
  const [inferiorGuard, setInferiorGuard] = useState(null);
  // Shape: { code: 'INFERIOR_SOURCE' | 'INFERIOR_VARIANT',
  //          message?: string, guidance?: object, busy: boolean } | null

  // RC 1.55 P0 (2026-05-12 PM) — post-terminal hydration-pending
  // state. When the post-terminal retry schedule on
  // `GET /api/interview-prep/{id}` exhausts, this is set so the UI
  // can surface a "Refreshing preview…" banner + Retry button. The
  // Retry button re-invokes the canonical READ-ONLY GET; it never
  // re-dispatches `/api/runs` and never re-charges.
  const [pendingHydration, setPendingHydration] = useState(null);
  const [retryingHydration, setRetryingHydration] = useState(false);

  // ── U-3.IP — Regenerate confirm gate (2026-02-15) ────────────────
  //
  // OPERATOR-LOCKED. Prevents the audit's IP-C cascade: the
  // Regenerate button creates a NEW charged artefact via the
  // `interview_prep:force` idempotency key. Before U-3, the FE
  // happily fired Regenerate even when a terminal IP already
  // existed for the job but had failed to bind to section state —
  // turning every IP-B failure into a stacked +15 credit charge.
  //
  // With U-3, if a terminal IP is bound (from any path — URL handle,
  // marker recovery, by-job fallback, post-terminal hydrate),
  // clicking the Generate CTA opens a confirm dialog. The user must
  // explicitly acknowledge the new spend before the dispatch fires.
  // First-time generation (no result bound) skips the dialog.
  //
  // Read-only gate; no charge, no dispatch, no LLM. Tier 2 will
  // generalise this to a workspace-wide regenerate confirmation
  // surface.
  const [regenerateConfirm, setRegenerateConfirm] = useState(null);
  // Shape: { force: boolean } | null

  const abortRef = useRef(null);

  // ── Lifecycle: do NOT abort polling on unmount ────────────────
  //
  // RC 1.52 Phase 3B.3 (operator-locked, 2026-05-11 PM) — the
  // previous unmount-cleanup unconditionally called
  // `abortRef.current.abort()`, which created a split-brain bug:
  // the frontend stopped listening while the backend coroutine
  // (guarded by `asyncio.shield` in the orchestrator) kept running
  // and successfully completed + charged. The user saw an
  // "aborted" toast despite having paid for a real deliverable.
  //
  // The new model: an in-flight sessionStorage marker carries the
  // run_id + idempotency_key across unmount/refresh. On remount,
  // the recovery effect polls the existing run row directly and
  // hydrates the result — no new charge.
  //
  // Aborts now ONLY fire when the user clicks Generate again
  // (intentional cancel of an in-flight run); see handleRun L379.

  // ── RC 1.51 — Click-gated localStorage rehydrate ────────────────
  //
  // Operator product rule (2026-02):
  //   • Workspace Interview Prep MUST be blind to baseline
  //     analyse_fit hydration. It must never auto-bind baseline
  //     diagnostic content. It must only become generated /
  //     unlocked after the user clicks Generate Interview Prep.
  //   • Refresh / section switch must restore the generated
  //     Interview Prep.
  //
  // Implementation: a single synchronous localStorage read keyed
  // by (user, job, source). The marker is only ever WRITTEN by
  // `handleRun` after a successful paid Workspace click — so the
  // presence of a payload here is proof the user has unlocked
  // this deliverable. No /best-fit fetch, no inflight marker, no
  // baseline reuse.
  //
  // This effect runs once per source change (cheap, no I/O), so
  // the restoring banner appears + clears in the same render
  // cycle and the result binds immediately on remount.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !sourceCvId) {
      setRestoring(false);
      return;
    }
    // Drop any stale result bound to a previous source.
    setResult((prev) => {
      if (!prev) return prev;
      if (prev.sourceType === backendSourceType && prev.sourceId === sourceCvId) return prev;
      return null;
    });
    const persisted = getInterviewPrepResult({
      userId:     primaryCv.user_id,
      jobId,
      sourceType: backendSourceType,
      sourceId:   sourceCvId,
    });
    if (persisted) {
      setResult(_resultFromInterviewPrepPayload(persisted, {
        sourceType: backendSourceType,
        sourceId:   sourceCvId,
      }));
      // RC 1.52 Phase 3B.1 — recovery-path credit refresh parity.
      // The wallet pill already reflects the original handleRun
      // debit, but this call guarantees server-truth match after a
      // tab-switch / remount, mirroring CvSection's Phase 3A.4 fix.
      if (typeof credits?.refresh === 'function') {
        try { credits.refresh(); } catch { /* noop */ }
      }
    }
    setRestoring(false);
  }, [jobId, primaryCv?.user_id, sourceCvId, backendSourceType]);

  // ── In-flight recovery on mount (RC 1.52 Phase 3B.3 Hotfix) ─────
  //
  // If the previous mount's generation was unmounted before reaching
  // a terminal state, the in-flight marker carries the run_id (if
  // we got to async_202) and/or the idempotency_key (always). On
  // remount:
  //   • If a run_id is in the marker, poll that run id directly.
  //     This is the cheapest recovery path — no new dispatch, no
  //     new charge risk.
  //   • If only an idempotency_key is in the marker (no run_id —
  //     dispatch was interrupted before 202), do nothing on mount.
  //     The next user click will re-dispatch with the SAME key and
  //     the orchestrator's replay short-circuit will return the
  //     existing row (if it persisted) with 0 charges, OR mint a
  //     fresh one (if it didn't).
  // Marker is expired-checked; stale markers are cleared.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    // Already have a result (URL-bound rehydrate ran or freshly
    // mounted with a previous in-memory result) — nothing to do.
    if (result?.interview_prep_id) return undefined;
    // Already running on this mount — let the in-flight dispatch
    // finish first.
    if (running) return undefined;
    const marker = readInflightInterviewPrepMarker(primaryCv.user_id, jobId);
    if (!marker) return undefined;
    if (isInflightInterviewPrepMarkerExpired(marker)) {
      clearInflightInterviewPrepMarker(primaryCv.user_id, jobId);
      return undefined;
    }
    if (!marker.runId) return undefined;

    let cancelled = false;
    // Phase -1 Tier 1 — S7 (operator-locked, 2026-02-15) — use the
    // RECOVERING surface, NOT `setRunning(true)`. The re-attach poll
    // is read-only and does not charge; flipping `running` would
    // disable the Generate button and the user would see a
    // "Generating…" spinner on a chargeable button for a non-
    // chargeable read. See OPERATION_LIFECYCLE_AUDIT.md Part 1
    // cause 1 + the F (Minimal patch plan) Patch 1.
    setRecovering(true);
    (async () => {
      try {
        const r = await pollInterviewPrepUntilTerminal(marker.runId, {});
        if (cancelled) return;
        const persisted = {
          interview_prep_id: r?.interview_prep_id || marker.runId,
          score:             r?.score || null,
          interview_prep:    r?.interview_prep || null,
          billing:           { ...(r?.billing || {}), cached: true },
          sourceType:        backendSourceType,
          sourceId:          sourceCvId,
          analysis:          null,
        };
        setResult(persisted);
        try {
          const _next = new URLSearchParams(searchParams);
          _next.set('interview_prep_id', persisted.interview_prep_id);
          if (typeof setSearchParams === 'function') {
            setSearchParams(_next, { replace: true });
          }
        } catch { /* noop */ }
        clearInflightInterviewPrepMarker(primaryCv.user_id, jobId);
        clearIdempotencyKey(primaryCv.user_id, jobId, 'interview_prep');
        clearIdempotencyKey(primaryCv.user_id, jobId, 'interview_prep:force');
        toast.success('Interview Prep ready — recovered after refresh.');
        if (typeof credits?.refresh === 'function') {
          try { credits.refresh(); } catch { /* noop */ }
        }
      } catch (err) {
        if (cancelled) return;
        // Recoverable / still-running — keep the marker for the
        // next mount and stay silent. Hard failure → clear marker.
        if (!err?.recoverable) {
          clearInflightInterviewPrepMarker(primaryCv.user_id, jobId);
        }
      } finally {
        // Phase -1 Tier 1 — S7 — clear the RECOVERING surface, NOT
        // `running` (which was never set on this path).
        if (!cancelled) setRecovering(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryCv?.user_id, sourceCvId, backendSourceType]);

  // ── U-2.IP — Canonical by-job mount fallback (2026-02-15) ────────
  //
  // OPERATOR-LOCKED. Closes the IP-B audit cause: when (a) the SS
  // marker is lost (hard refresh / private mode / mobile background
  // tab kill), AND (b) there is no `?interview_prep_id` URL handle
  // (only stamped on terminal success), AND (c) E2 above has nothing
  // to poll → the user was previously stranded with "charged but no
  // result" because no FE code path queried the canonical
  // `application_documents` row for "the latest IP for this job".
  //
  // This effect runs ONCE per (user, jobId) when the section mounts
  // with no marker/URL/state — and queries the new
  // `GET /api/interview-prep/by-job/{jobId}?latest=true` server-side
  // filter. If a terminal IP exists, we stamp the URL handle so the
  // existing URL-bound rehydrate (next effect) picks it up via the
  // canonical hydrator path — no duplicate code, no second fetch.
  //
  // Read-only. NEVER dispatches. NEVER charges. Server-side filter is
  // owner-scoped via RLS on application_documents.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    // Already have a result OR a URL handle OR a marker → existing
    // paths cover it; skip the canonical fallback.
    if (result?.interview_prep_id) return undefined;
    if (searchParams.get('interview_prep_id')) return undefined;
    const _marker = (() => {
      try { return readInflightInterviewPrepMarker(primaryCv.user_id, jobId); }
      catch { return null; }
    })();
    if (_marker?.runId) return undefined;
    if (running) return undefined;

    let cancelled = false;
    (async () => {
      try {
        // U-12.FE.C — source-context filter (2026-02-15).
        // OPERATOR-LOCKED. By-job lookup now respects the current
        // CV context selection. Without this, the lookup would
        // return ANY IP for the job — even one tied to a different
        // CV variant the user is not currently working with.
        //
        // U-13.IP.A (2026-02-15) — FIX: pre-U-13 this used
        // `backendSourceType` (which is `'cv_variant'|'uploaded_cv'`,
        // NEVER `'base_cv'`), so the base_cv branch never fired and
        // the cold-mount by-job call went out unfiltered. Now we
        // read the URL-canonical `sourceCvType` directly
        // (`'cv_variant'|'base_cv'`), matching the backend's filter
        // vocabulary. Symmetric to the U-4.IP live binder site
        // below.
        const _ctxQs = new URLSearchParams();
        _ctxQs.set('latest', 'true');
        if (sourceCvType === 'cv_variant' && sourceCvId) {
          _ctxQs.set('cv_variant_id', sourceCvId);
        } else if (sourceCvType === 'base_cv' && sourceCvId) {
          _ctxQs.set('base_cv_id', sourceCvId);
        }
        const env = await apiFetch(
          `/api/interview-prep/by-job/${encodeURIComponent(jobId)}?${_ctxQs.toString()}`,
        );
        if (cancelled) return;
        if (!env || env.found !== true) return;
        const ipId = env.interview_prep_id || env.result?.interview_prep_id;
        if (!ipId) return;
        // Stamp URL handle — the URL-bound rehydrate effect below
        // will pick it up and bind via the canonical hydrator
        // path. We deliberately do NOT call verifyAndHydrate here
        // because the URL-bound effect already owns that wiring
        // (post-terminal hydrator + thin-payload race coverage).
        try {
          const _next = new URLSearchParams(searchParams);
          _next.set('interview_prep_id', ipId);
          if (typeof setSearchParams === 'function') {
            setSearchParams(_next, { replace: true });
          }
        } catch { /* noop */ }
      } catch (err) {
        // 404 / 401 / table missing / network — silently degrade.
        // The SS-marker path + URL handle remain authoritative.
        actionTrace('interview_prep', 'by_job_lookup_failed', {
          code: err?.code, status: err?.status,
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryCv?.user_id, sourceCvId, backendSourceType]);

  // ── U-4.IP — Live post-terminal binder (2026-02-15) ─────────────
  //
  // OPERATOR-LOCKED. Closes the live-return binding gap surfaced by
  // operator smoke (2026-02-15) on the U-1/U-2/U-3 patch ship:
  //
  // Scenario: user clicks Generate → 202 async → tab-switches mid-run
  // → backend tail completes (U-1.IP mirror correctly lifts the
  // operations row + application_documents row to `complete`) → the
  // polling helper raises `recoverable=true` after the 120 s hard
  // cap → handleRun's catch toasts "still generating in background"
  // and RETURNS without binding (L1070-1077). User returns to tab:
  // toast surfaces completion but section state remains empty.
  // The pre-existing `useArtefactRehydrate` (URL-bound, single
  // canonical GET) is gated on `urlIpId` AND on a strict source-match
  // path that can no-op silently when the row is reachable but the
  // FE state has drifted. Hard refresh works because the mount-time
  // by-job fallback (U-2.IP) re-runs from a clean slate.
  //
  // The fix: a long-lived watcher that — whenever the section is
  // mounted, has NO result bound, has a marker present OR is
  // recovering — periodically AND on every visibilitychange hits
  // the new canonical `GET /api/interview-prep/by-job/{jobId}?
  // latest=true` endpoint, and on terminal-found re-hydrates the
  // section via the SAME canonical hydrator path the dispatch
  // success branch uses (verifyAndHydrateInterviewPrep + setResult
  // + lifecycleMarkComplete + pack:artefact_ready + clear marker).
  //
  // READ-ONLY. NEVER dispatches. NEVER charges. Stops itself the
  // moment `result?.interview_prep_id` becomes truthy (effect dep
  // change → cleanup).
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    // Already bound → nothing to do; the dispatch branch / URL-bound
    // rehydrate has already won the race.
    if (result?.interview_prep_id) return undefined;

    // Only run the watcher when there's a reason to believe a run
    // is (or was) in flight: marker present, OR recovering surface
    // on, OR running surface on. Without one of these signals,
    // there's nothing to bind — the mount fallback at L499 handled
    // the cold-start case.
    let _hasMarker = false;
    try {
      const m = readInflightInterviewPrepMarker(primaryCv.user_id, jobId);
      _hasMarker = !!(m && !isInflightInterviewPrepMarkerExpired(m));
    } catch { /* noop */ }
    if (!_hasMarker && !recovering && !running) return undefined;

    let cancelled = false;
    let timer = null;
    let lock = false;

    const HEARTBEAT_MS = 12000;

    async function tryBind() {
      if (cancelled || lock) return;
      // Re-read latest state under the lock — a concurrent dispatch
      // success branch may have just bound the result.
      lock = true;
      try {
        // U-12.FE.C — source-context filter on the live binder too.
        // Without this, the binder could rebind an IP tied to a
        // different CV variant during a tab-switch return.
        //
        // U-13.IP.A (2026-02-15) — FIX: see L546 commentary. Use
        // the URL-canonical `sourceCvType` (`'cv_variant'|'base_cv'`)
        // instead of the legacy `backendSourceType` which only ever
        // holds `'cv_variant'|'uploaded_cv'`.
        const _ctxQs = new URLSearchParams();
        _ctxQs.set('latest', 'true');
        if (sourceCvType === 'cv_variant' && sourceCvId) {
          _ctxQs.set('cv_variant_id', sourceCvId);
        } else if (sourceCvType === 'base_cv' && sourceCvId) {
          _ctxQs.set('base_cv_id', sourceCvId);
        }
        // Step 1 — canonical by-job lookup. Read-only; never charges.
        const env = await apiFetch(
          `/api/interview-prep/by-job/${encodeURIComponent(jobId)}?${_ctxQs.toString()}`,
        );
        if (cancelled || !env || env.found !== true) return;
        const ipId = env.interview_prep_id || env.result?.interview_prep_id;
        if (!ipId) return;

        // Step 2 — fetch the full canonical envelope to get the
        // `interview_prep` payload. Use the canonical hydrator —
        // identical to the dispatch-success path.
        let envelope;
        try {
          envelope = await verifyAndHydrateInterviewPrep({
            envelope: { interview_prep_id: ipId, interview_prep: null },
            ipId,
            apiGet:   (path, opts) => apiFetch(path, opts),
          });
        } catch (hydrateErr) {
          // Row not yet readable — silently retry on next heartbeat.
          if (hydrateErr?.recoverable === true) return;
          // Hard failure — log and stop trying for this cycle.
          actionTrace('interview_prep', 'live_binder_hydrate_failed', {
            ipId, code: hydrateErr?.code,
          });
          return;
        }
        if (cancelled) return;
        const res = envelope?.result || envelope;
        const ip  = res?.interview_prep;
        if (!ip || typeof ip !== 'object' || Object.keys(ip).length === 0) {
          // Hydrator returned a thin envelope. Retry on next heartbeat.
          return;
        }

        // Step 3 — bind via the canonical setter shape. Identical
        // to the dispatch success branch (L946-959).
        const persisted = {
          interview_prep_id: ipId,
          score:             res?.score || null,
          interview_prep:    ip,
          billing:           { ...(envelope?.billing || {}), cached: true },
          sourceType:        backendSourceType,
          sourceId:          sourceCvId,
          analysis:          null,
        };
        setResult(persisted);

        // Stamp the URL handle for fast rebind on next mount.
        try {
          const _next = new URLSearchParams(searchParams);
          if (_next.get('interview_prep_id') !== ipId) {
            _next.set('interview_prep_id', ipId);
            if (typeof setSearchParams === 'function') {
              setSearchParams(_next, { replace: true });
            }
          }
        } catch { /* noop */ }

        // Side-effects parity with the dispatch success branch.
        try {
          clearInflightInterviewPrepMarker(primaryCv.user_id, jobId);
          clearIdempotencyKey(primaryCv.user_id, jobId, 'interview_prep');
          clearIdempotencyKey(primaryCv.user_id, jobId, 'interview_prep:force');
        } catch { /* noop */ }
        try { setRecovering(false); } catch { /* noop */ }
        try { if (typeof credits?.refresh === 'function') credits.refresh(); }
        catch { /* noop */ }
        try { cacheInvalidate(['interview-prep:all', 'jobs', 'dashboard:bundle']); }
        catch { /* noop */ }
        try {
          controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
            jobId,
            actionCode: 'interview_prep',
            artefactId: ipId,
            reused: true,
          });
        } catch { /* noop */ }
        try {
          lifecycleMarkComplete({ interviewPrepId: ipId, reused: true });
        } catch { /* noop */ }
        toast.success('Interview Prep ready.');
        actionTrace('interview_prep', 'live_binder_bound', { ipId });
      } catch (err) {
        // Network / 401 / 404 / table missing — silently retry on
        // next heartbeat. The watcher is best-effort.
        actionTrace('interview_prep', 'live_binder_check_failed', {
          code: err?.code, status: err?.status,
        });
      } finally {
        lock = false;
      }
    }

    function onVisible() {
      if (typeof document !== 'undefined'
          && document.visibilityState === 'visible') {
        // Debounce: run after 250 ms so a rapid lock/unlock cycle
        // doesn't fire twice.
        if (timer) clearTimeout(timer);
        timer = setTimeout(tryBind, 250);
      }
    }

    // 1) Try once immediately on effect mount.
    tryBind();
    // 2) Heartbeat — every 12 s while the watcher is active.
    const heartbeat = setInterval(() => {
      if (typeof document === 'undefined'
          || document.visibilityState === 'visible') {
        tryBind();
      }
    }, HEARTBEAT_MS);
    // 3) Visibilitychange — tab-return triggers a debounced rebind.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      clearInterval(heartbeat);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobId,
    primaryCv?.user_id,
    result?.interview_prep_id,
    recovering,
    running,
    sourceCvId,
    backendSourceType,
  ]);

  // ── URL-bound rehydrate (RC 1.52 Phase 3B.3) ───────────────────
  //
  // When `?interview_prep_id={id}` is in the URL, fetch the
  // canonical row from the standalone GET endpoint and bind it.
  // This is the post-redesign rebind path — replaces the previous
  // localStorage payload cache. The application_documents row is
  // the durable source of truth.
  //
  // RC 1.55 U2 (operator-locked, 2026-05-13) — extracted into a
  // stable `useCallback` so it can be driven by BOTH the existing
  // mount-time URL effect AND the new `useArtefactRehydrate` hook
  // (visibility / popstate). Logic is byte-equivalent to the prior
  // URL effect — only the shape changed. READ-ONLY canonical GET.
  // No charge. No dispatch. Honours every prior alignment guard.
  const urlIpId = searchParams.get('interview_prep_id');
  const refetchInterviewPrepFromUrl = useCallback(async () => {
    if (!urlIpId) return;
    if (!jobId || !primaryCv?.user_id || !sourceCvId) return;
    // Already bound to this id — short-circuit.
    if (result?.interview_prep_id === urlIpId) return;
    if (running) return;
    try {
      const row = await apiFetch(`/api/interview-prep/${urlIpId}`, {
        method:  'GET',
        headers: { 'X-Envelope': 'rc_1_52_canonical' },
      });
      if (!row || (row.status || '').toLowerCase() !== 'complete') return;
      const res = row.result || {};
      // Strict source-match guard — refuse a row that does not
      // belong to the currently-selected (jobId) pair.
      const rowJobId   = res?.job_id || row?.job_id;
      if (rowJobId && rowJobId !== jobId) return;
      // ── U-13.IP.B (2026-02-15) — operator-locked re-instate. ──
      // PRE-U-13 (under U-10.IP.B) the cv_variant_id guard was
      // intentionally removed so any IP existing for the job would
      // bind. Operator smoke 2026-02-16 surfaced this as the
      // "binds too broadly" defect: an IP tied to variant-B was
      // surfacing under variant-A's workspace.
      //
      // Re-instated behaviour:
      //   • If `?from_pack` is present → SOURCE-TOLERANT. The pack
      //     explicitly carries `?interview_prep_id=X` as part of
      //     its stored composition and the pack's ID set is the
      //     authoritative intent. Bind X as-is.
      //   • Otherwise → STRICT context match. The IP's
      //     `linked_cv_variant_id` MUST equal the current
      //     `cvVariantId` (when sourceCvType='cv_variant'). When
      //     sourceCvType='base_cv', the IP's `linked_cv_variant_id`
      //     MUST be null AND its `linked_base_cv_id` MUST equal
      //     the current `sourceCvId`.
      //
      // The canonical envelope (interview_prep_orchestrator.
      // build_poll_envelope) surfaces `linked_cv_variant_id`. We
      // ALSO require `linked_base_cv_id` (added in U-13.BE) so
      // the base-CV branch can validate alignment.
      const _fromPack = searchParams.get('from_pack') !== null;
      if (!_fromPack) {
        const linkedVariantId = res?.linked_cv_variant_id ?? null;
        const linkedBaseCvId  = res?.linked_base_cv_id ?? null;
        if (sourceCvType === 'cv_variant') {
          // Tailored-variant context — IP MUST be linked to the
          // exact variant the user has selected.
          if (linkedVariantId !== sourceCvId) {
            actionTrace('interview_prep', 'url_bound_rebind_ctx_mismatch', {
              ipId: urlIpId,
              expected: { sourceCvType, sourceCvId },
              actual:   { linkedVariantId, linkedBaseCvId },
            });
            return;
          }
        } else {
          // Source-CV context — IP MUST be linked directly to the
          // base CV (no variant in between).
          if (linkedVariantId !== null) {
            actionTrace('interview_prep', 'url_bound_rebind_ctx_mismatch', {
              ipId: urlIpId,
              expected: { sourceCvType, sourceCvId },
              actual:   { linkedVariantId, linkedBaseCvId },
            });
            return;
          }
          if (linkedBaseCvId && sourceCvId && linkedBaseCvId !== sourceCvId) {
            actionTrace('interview_prep', 'url_bound_rebind_ctx_mismatch', {
              ipId: urlIpId,
              expected: { sourceCvType, sourceCvId },
              actual:   { linkedVariantId, linkedBaseCvId },
            });
            return;
          }
        }
      }
      setResult({
        interview_prep_id: res.interview_prep_id || urlIpId,
        score:             res.score || null,
        interview_prep:    res.interview_prep || null,
        billing:           { ...(row.billing || {}), cached: true },
        sourceType:        backendSourceType,
        sourceId:          sourceCvId,
        // U-13.AUDIT — surface row context fingerprint into result
        // so the rendered card DOM exposes both expected & actual
        // CV context (operator audit visibility).
        linked_cv_variant_id: res?.linked_cv_variant_id ?? null,
        linked_base_cv_id:    res?.linked_base_cv_id ?? null,
        analysis:          null,
      });
      // RC 1.55 Phase 1 (operator-locked, 2026-05-13) — terminal
      // side-effects on successful new canonical rebind. Mirrors
      // the dispatch path's post-terminal block so the wallet pill
      // / Documents list / Pack badge stay coherent after a
      // tab-return-only completion. READ-ONLY surface — no new
      // POST, no generation re-dispatch, no charge-settlement
      // re-call. Fail-open per-call.
      try { if (typeof credits?.refresh === 'function') credits.refresh(); }
      catch { /* noop */ }
      try { cacheInvalidate(['interview-prep:all', 'jobs', 'dashboard:bundle']); } catch { /* noop */ }
      try {
        controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
          jobId,
          actionCode: 'interview_prep',
          artefactId: res.interview_prep_id || urlIpId,
          reused: true,
        });
      } catch { /* noop */ }
    } catch {
      /* noop — URL-bound rebind is best-effort */
    }
  }, [urlIpId, jobId, primaryCv?.user_id, sourceCvId, backendSourceType, running, result?.interview_prep_id]);

  // Mount-time URL effect — unchanged contract, now delegates to
  // the shared callback. Runs once per dependency change.
  useEffect(() => {
    if (!urlIpId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refetchInterviewPrepFromUrl();
    })();
    return () => { cancelled = true; };
  }, [refetchInterviewPrepFromUrl, urlIpId]);

  // RC 1.55 U2 — visibility / popstate-driven re-fetch. The hook
  // installs `visibilitychange` + `popstate` listeners and calls
  // `refetchInterviewPrepFromUrl()` on tab-return or browser
  // back/forward. Gated off while a run is in-flight so the
  // dispatch's own binding path always wins. READ-ONLY. No POST.
  useArtefactRehydrate({
    enabled: !!urlIpId
      && !!jobId
      && !!primaryCv?.user_id
      && !!sourceCvId
      && !running,
    fetchAndBind: refetchInterviewPrepFromUrl,
  });

  // ── RC 1.51 Pack Separation (operator-locked, 2026-02) ──────────
  //
  // The legacy `/best-fit` mount-hydrate, the URL-driven Layer-A
  // rehydrate, and the inflight-marker `/best-fit` recovery loop
  // have all been REMOVED. They auto-bound baseline diagnostic
  // analyse_fit content as if it were the workspace deliverable,
  // breaking the operator's product rule:
  //
  //     • Workspace Interview Prep MUST be blind to baseline
  //       analyse_fit hydration.
  //     • It must never auto-bind baseline diagnostic content.
  //     • It must only become generated/unlocked after the user
  //       clicks Generate Interview Prep.
  //
  // The single click-gated localStorage rehydrate above (one
  // synchronous read on mount) is the ONLY restoration path. The
  // marker is written exclusively by `handleRun` after a
  // successful paid `interview_prep` run.

  // ── Pre-flight credits gate (toast-only Phase 3) ─────────────────
  const ensureCreditsAvailable = (actionType, actionLabel) => {
    if (creditsLoading) return false;
    const cost = getActionCost(usageCosts, actionType);
    if (cost == null || cost === 0) return true;
    const available =
      Number(credits?.includedUsageRemaining || 0)
      + Number(credits?.extraUsageRemaining || 0);
    if (available >= cost) return true;
    toast.error(`Not enough Credits for ${actionLabel}`, {
      description: `${available} available · ${cost} needed. Open Plan to add Credits.`,
      action: {
        label: 'View plan',
        onClick: () => {
          try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
        },
      },
    });
    return false;
  };

  // ── Run / Re-run handler ─────────────────────────────────────────
  //
  // RC 1.51 follow-up #3 (operator-locked, 2026-02) — defensive
  // user-click gate. `handleRun` REFUSES to fire unless the caller
  // explicitly passes `userInitiated: true`. There are exactly TWO
  // legitimate callers:
  //   1. The "Generate Interview Prep" button (L939+).
  //   2. The "Analyse anyway" button inside the inferior-source
  //      confirmation panel (L585+).
  // Both pass `userInitiated: true`. This belt-and-braces guard
  // ensures no future mount effect, focus handler, or URL-driven
  // rehydrate path can accidentally trigger a chargeable run.
  //
  // `opts.force` (RC 1.49.7 H2) bypasses the backend's R2/R3
  // inferior-source / inferior-variant reject-gate by passing
  // `force_override: true`. Only the "Analyse anyway" button uses
  // this path.
  //
  // RC 1.55 P0 (2026-05-12 PM) — pending-hydration retry handler.
  // When the post-terminal canonical GET retry schedule exhausts,
  // this fires from the Refresh-preview button. READ-ONLY GET.
  // Never re-dispatches `/api/runs`. Never re-charges.
  const handleRetryHydration = async () => {
    const id = pendingHydration?.ipId || result?.interview_prep_id;
    if (!id) return;
    setRetryingHydration(true);
    try {
      const hydrated = await verifyAndHydrateInterviewPrep({
        envelope: null,
        ipId:     id,
        apiGet:   (path, opts) => apiFetch(path, opts),
      });
      const res = hydrated?.result || hydrated;
      const ip  = res?.interview_prep;
      if (!ip || typeof ip !== 'object' || Object.keys(ip).length === 0) {
        toast.info('Still saving. Try again in a few seconds.');
        return;
      }
      setResult((prev) => ({
        ...(prev || {}),
        interview_prep_id: id,
        interview_prep:    ip,
        score:             res.score || prev?.score || null,
        billing:           { ...(hydrated?.billing || prev?.billing || {}), cached: true },
        analysis:          null,
      }));
      setPendingHydration(null);
      toast.success('Interview prep ready.');
    } catch (e) {
      if (e?.recoverable === true) {
        toast.info(
          'Interview prep is still saving. Please retry in a few seconds'
          + ' or open Documents → Interview Prep.',
        );
      } else {
        toast.error(e?.message || 'Could not refresh preview.');
      }
    } finally {
      setRetryingHydration(false);
    }
  };

  const handleRun = async (opts = {}) => {
    if (opts.userInitiated !== true) {
      // Hard gate. A non-user-initiated call reaching this path is
      // a programming error in a future contributor's effect; we
      // refuse to charge and surface a console warning. No toast —
      // the user did not click anything.
      // eslint-disable-next-line no-console
      console.warn(
        '[InterviewPrep] handleRun blocked — userInitiated flag missing. '
        + 'Workspace Interview Prep only runs on explicit Generate button click.',
      );
      return;
    }
    const force = opts.force === true;
    if (!sourceCvId) {
      toast.error('Select a CV first (go back to the CV step).');
      return;
    }
    // RC 1.51 — Workspace Interview Prep is its own product action
    // with its own ledger line. Wallet preflight uses the
    // `interview_prep` price (admin-editable), NOT analyze_fit.
    if (!ensureCreditsAvailable('interview_prep', 'Interview Prep')) return;

    // Abort any prior in-flight polling (e.g. user clicked twice).
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    abortRef.current = new AbortController();

    // RC 1.49.7 H2 — when this path is reached via "Analyse anyway"
    // we park the force flag on the inferiorGuard so the render's
    // "Analysing…" affordance can show the busy state. The standard
    // Run path leaves inferiorGuard untouched.
    if (force) {
      setInferiorGuard((g) => (g ? { ...g, busy: true } : g));
    }

    setRunning(true);
    setProgressLabel('dispatching');
    actionTrace('interview_prep', 'dispatch_start', { jobId, force: !!force });
    try {
      // RC 1.52 Phase 3B.3 (operator-locked, 2026-05-11 PM) —
      // Standalone Interview Prep. NO MORE piggy-back on Analyse
      // Fit. The new helper calls `POST /api/interview-prep/generate`
      // directly. Charging is owned end-to-end by the standalone
      // orchestrator inside `credits_svc.charged()`.
      if (!job?.description?.trim()) {
        toast.error('Job description is missing. Reload the page.');
        setRunning(false);
        return;
      }

      // RC 1.52 Phase 3B.3 Hotfix — stable idempotency key. Same
      // (user, job) tuple reuses the same key for 10 min, so a
      // retry after abort/refresh hits the orchestrator's replay
      // short-circuit and returns the existing complete row with
      // billing.idempotent=true (0 new charge). Force flag mints
      // a fresh key so explicit regenerate charges legitimately.
      const idemAction = force ? 'interview_prep:force' : 'interview_prep';
      if (force) {
        // Force-regenerate path always mints fresh.
        clearIdempotencyKey(primaryCv?.user_id, jobId, idemAction);
      }
      const idempotencyKey = getOrMintIdempotencyKey(
        primaryCv?.user_id, jobId, idemAction,
      );

      // Write the in-flight marker BEFORE dispatch so an abort
      // between POST and the run_id callback still leaves a
      // breadcrumb for remount recovery.
      writeInflightInterviewPrepMarker(primaryCv?.user_id, jobId, {
        idempotencyKey,
        runId:      null,
        sourceCvId,
      });

      // RC 1.53 Phase 4.3A — dispatch through the
      // OperationLifecycleController. The adapter forwards the
      // existing `generateInterviewPrepWithPolling` call verbatim
      // (including the `onRunIdReady` synchronous URL write); the
      // controller gives us STABILIZING status, workspace-wide
      // Generate-lock, and the visible activity indicator. The
      // polling helper, idempotency-key derivation, billing
      // behaviour, and inflight-marker contract are UNCHANGED.
      const r = await controllerDispatch({
        jobId,
        actionCode: 'interview_prep',
        idempotencyKey,
        run: async ({ callbacks }) => interviewPrepAdapter.run({
          requestArgs: {
            jobId,
            jobText:         job.description,
            sourceType:      backendSourceType,
            sourceId:        sourceCvId,
            baseCvId:        primaryCv?.id,
            title:           (job?.title || 'Interview Prep'),
            idempotencyKey,
            forceRegenerate: !!force,
            signal:          abortRef.current.signal,
            onRunIdReady: (runId) => {
              if (!runId) return;
              setProgressLabel('polling');
              patchInflightInterviewPrepRunId(primaryCv?.user_id, jobId, runId);
              try {
                const _next = new URLSearchParams(searchParams);
                _next.set('interview_prep_id', runId);
                if (typeof setSearchParams === 'function') {
                  setSearchParams(_next, { replace: true });
                }
              } catch { /* noop */ }
            },
          },
          callbacks,
        }),
      });
      // No-op replay (dispatch returned existing terminal envelope
      // without re-invoking `run`): nothing more to do. The
      // remount/recovery effect at L308 already hydrated UI state.
      //
      // Phase -1 Tier 1 — S7 (operator-locked, 2026-02-15) — BUT the
      // in-flight marker + idempotency keys from the prior attempt
      // MUST be cleared on this idempotent-replay path, otherwise
      // the marker leaks and the next mount's recovery effect
      // re-polls indefinitely (causing the perception of
      // "auto-runs on every section entry"). See
      // OPERATION_LIFECYCLE_AUDIT.md Part 1 cause 5 + F Patch 2.
      if (!r) {
        try {
          clearInflightInterviewPrepMarker(primaryCv?.user_id, jobId);
          clearIdempotencyKey(primaryCv?.user_id, jobId, 'interview_prep');
          clearIdempotencyKey(primaryCv?.user_id, jobId, 'interview_prep:force');
        } catch { /* noop */ }
        return;
      }

      // RC 1.55 P0 (operator-locked, 2026-05-12 PM) — post-deploy
      // hydration race fix. The dispatch envelope can be thin
      // (null `interview_prep`) in the polling-helper's terminal-
      // poll race window — the durable application_documents row
      // IS committed and the charge has succeeded, but it is not
      // yet readable at the millisecond the terminal poll resolves.
      // Previously the section bound `interview_prep: null` and
      // rendered nothing until the user navigated away and back.
      //
      // The verifier performs an authoritative READ-ONLY GET on
      // `/api/interview-prep/{id}` (canonical envelope header)
      // with the operator-locked retry schedule [0, 750, 1500,
      // 3000] ms. It never re-dispatches, never re-charges.
      const _ipId = r?.interview_prep_id || r?.run_id;
      let hydratedIp = r?.interview_prep ?? null;
      let hydratedScore = r?.score || null;
      let pendingHydrationFlag = false;
      if (_ipId) {
        setProgressLabel('hydrating');
        try {
          const hydrated = await verifyAndHydrateInterviewPrep({
            envelope: r,
            ipId:     _ipId,
            apiGet:   (path, opts) => apiFetch(path, opts),
          });
          // Hydrator returns either the original envelope (when
          // fresh) or the canonical row whose `result` block
          // carries `interview_prep`, `score`, etc. Both are
          // accepted shapes — flatten here.
          const res = hydrated?.result || hydrated;
          if (res?.interview_prep && typeof res.interview_prep === 'object'
              && Object.keys(res.interview_prep).length > 0) {
            hydratedIp = res.interview_prep;
            hydratedScore = res.score || hydratedScore;
          }
        } catch (hydrateErr) {
          actionTrace('interview_prep', 'post_terminal_hydrate_failed', {
            ipId: _ipId, code: hydrateErr?.code,
          });
          // Calm fail-open. The artefact IS saved + charged. Set the
          // pending flag so the section surfaces the "Refreshing
          // preview…" banner with a Retry button (READ-ONLY).
          if (hydrateErr?.recoverable === true) {
            pendingHydrationFlag = true;
          } else {
            throw hydrateErr;
          }
        }
      }

      const interviewPrepPayload = hydratedIp;
      const billing = r?.billing ?? { cached: false };
      const persisted = {
        interview_prep_id: _ipId || null,
        score:           hydratedScore,
        interview_prep:  interviewPrepPayload,
        billing,
        sourceType:      backendSourceType,
        sourceId:        sourceCvId,
      };
      setResult({
        ...persisted,
        // analysis intentionally null — the workspace surface no
        // longer renders fit numbers (RC 1.50 R.3).
        analysis: null,
      });
      setPendingHydration(pendingHydrationFlag ? { ipId: _ipId } : null);
      // RC 1.52 Phase 3B.3 — synchronous URL write so refresh /
      // section-switch / tab-return rebinds via the canonical
      // standalone GET endpoint, not via a localStorage payload
      // cache. The previous `markInterviewPrepUnlocked` localStorage
      // path is removed — the application_documents row IS the
      // durable source of truth.
      if (_ipId) {
        try {
          const _next = new URLSearchParams(searchParams);
          _next.set('interview_prep_id', _ipId);
          if (typeof setSearchParams === 'function') {
            setSearchParams(_next, { replace: true });
          }
        } catch { /* noop */ }
      }
      // RC 1.49.7 Slice 6b — invalidate wizard-parity list keys so
      // JobsPage / DashboardPage refetch and refresh wallet-derived
      // state.
      // RC 1.59 Slice 4 — also sweep `interview-prep:all` so the
      // Documents tab (`InterviewPrepHistory`) sees the new prep on
      // the next mount without waiting for its 5-minute TTL.
      cacheInvalidate(['interview-prep:all', 'jobs', 'dashboard:bundle']);
      // RC 1.49.7 H2 — a successful run (including a forced one)
      // retires the inline confirmation.
      setInferiorGuard(null);

      // RC 1.52 Phase 3B.3 Hotfix — clear the idempotency key AND
      // the in-flight marker on confirmed success, so the next
      // legitimate generation gets a fresh key (otherwise force-
      // regenerate would race the cached key on retry).
      try {
        clearIdempotencyKey(primaryCv?.user_id, jobId, 'interview_prep');
        clearIdempotencyKey(primaryCv?.user_id, jobId, 'interview_prep:force');
        clearInflightInterviewPrepMarker(primaryCv?.user_id, jobId);
      } catch { /* noop */ }
      // operator #6 — cache-hit must consume 0 usage. Surface a calm
      // copy distinct from a fresh paid run.
      if (pendingHydrationFlag) {
        // RC 1.55 P0 — hydration race fail-open. Artefact saved +
        // charged, but the canonical GET is still incomplete. The
        // section's banner + retry button (rendered below) handle
        // the recovery; the toast just keeps the user informed.
        toast.info(
          'Interview prep generated and saved. Refreshing preview…',
          { duration: 6000 },
        );
      } else if (r?.billing?.cached || r?.billing?.idempotent || r?.idempotent_replay) {
        toast.success('Existing interview prep loaded — no Credits charged.');
      } else {
        toast.success('Interview Prep ready.');
      }
      // RC 1.53 Phase 4.3A — AWAIT credits.refresh so the activity
      // indicator stays in `stabilizing` ("Updating your
      // documents…") until the wallet pill is re-read from server.
      setProgressLabel('stabilizing');
      if (typeof credits?.refresh === 'function') {
        try { await credits.refresh(); } catch { /* noop */ }
      }
      actionTrace('interview_prep', 'credits_refresh_completed', {});
      // RC 1.53 Phase 4.3A — emit pack:artefact_ready so the Pack
      // section's readiness gate re-derives without manual user
      // action. Cache-hit / fresh-debit both surface this — the
      // row exists either way.
      try {
        controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
          jobId,
          actionCode: 'interview_prep',
          artefactId: r?.interview_prep_id || r?.run_id || null,
          reused: !!(r?.billing?.cached || r?.billing?.idempotent || r?.idempotent_replay),
        });
      } catch { /* noop */ }
      // RC 1.52 Phase 3B.1 — start the 5-min cooldown only on a
      // confirmed successful dispatch (paid run OR cached). The
      // catch branches below all early-return, so a failed run
      // can't reach this line.
      try {
        startCooldown(interviewPrepCooldownKey(
          primaryCv.user_id, jobId, sourceCvId,
        ));
      } catch { /* noop */ }
      setProgressLabel(null);
      // RC 1.53 Phase 4.3A — signal terminal completion to the
      // controller AFTER credits refresh + URL write + cache
      // invalidate + cooldown have landed. The activity indicator
      // unmounts ONLY now — "first visible, last gone".
      try {
        lifecycleMarkComplete({
          interviewPrepId: r?.interview_prep_id || r?.run_id || null,
          reused: !!(r?.billing?.cached || r?.billing?.idempotent || r?.idempotent_replay),
        });
      } catch { /* noop */ }
      actionTrace('interview_prep', 'operation_closed', {
        interviewPrepId: r?.interview_prep_id || r?.run_id || null,
      });
    } catch (err) {
      // RC 1.52 Phase 3B.3 Hotfix — recovery-aware error handling.
      //
      // The polling helper raises `recoverable=true` for:
      //   • signal abort (user navigated / section unmount race)
      //   • hard-cap 120 s timeout (backend still running)
      //   • mid-poll network blip that exhausts the cap
      // These MUST NOT show "aborted" / "failed" failure toasts —
      // the backend may still complete and charge for a real
      // deliverable. The in-flight marker carries the run_id +
      // idempotency_key across the unmount; the next mount /
      // refresh rebinds via the URL or by polling the run_id
      // directly. A retry (Generate clicked again) re-dispatches
      // with the SAME idempotency_key and hits the orchestrator's
      // replay short-circuit — 0 new charge.
      if (err?.recoverable === true || err?.name === 'AbortError') {
        // U-4.IP — flip the RECOVERING surface ON so the live
        // post-terminal binder (effect above L499 area) is enabled
        // and starts its heartbeat + visibilitychange watch
        // immediately. Without this flip, the binder would only
        // activate if a SS marker is present — which is fragile
        // because the marker can race with the dispatch promise.
        try { setRecovering(true); } catch { /* noop */ }
        toast.info(
          'Interview Prep is still generating in the background. '
          + 'It will appear automatically when ready — '
          + 'no need to re-click.',
        );
        return;
      }
      // RC 1.49.7 H2 — inline inferior-source / inferior-variant
      // 409 confirmation. Catch BEFORE handleCreditsError /
      // generic toast so this path wins. Backend's R2/R3 check
      // runs BEFORE consume_usage (jobs.py:1721), so no credits
      // were spent — preserves the "failed pre-persist = zero
      // usage" invariant.
      const code = err?.data?.detail?.code;
      if (err?.status === 409
          && (code === 'INFERIOR_SOURCE' || code === 'INFERIOR_VARIANT')) {
        setInferiorGuard({
          code,
          message:  err?.data?.detail?.message || null,
          guidance: err?.data?.detail?.guidance || {},
          busy:     false,
        });
        return;
      }
      if (handleCreditsError(err, {
        action: 'Interview Prep',
        refreshCredits: credits?.refresh,
      })) return;
      // operator #7 — failed pre-persist run consumes 0 usage. The
      // backend already protects this; nothing extra fires here.
      // Hard-failure path only — clear the in-flight marker so a
      // subsequent dispatch doesn't try to recover into a dead run.
      try {
        clearInflightInterviewPrepMarker(primaryCv?.user_id, jobId);
      } catch { /* noop */ }
      toast.error(err?.message || 'Interview Prep failed.');
    } finally {
      setRunning(false);
      setProgressLabel(null);
      // Clear the force-busy flag on the guard if the forced run
      // ended (either resolved above via setInferiorGuard(null) or
      // errored out — in which case a fresh decision is required).
      setInferiorGuard((g) => (g ? { ...g, busy: false } : g));
      // RC 1.53 Phase 4.3A — defensive controller resync. Idempotent
      // no-op on already-terminal ops.
      try { lifecycleMarkFailed({ message: 'generation_path_exited' }); }
      catch { /* noop */ }
    }
  };

  // ── Render helpers ───────────────────────────────────────────────
  const cached = !!result?.billing?.cached;
  const wasRecovered = !!result?.billing?._recovered_inflight;

  // RC 1.50 R.3 — derive the 5-bucket Interview Prep shape from
  // the persisted `interview_prep` payload. Pure mapper; tolerates
  // null / partial / legacy payloads (returns an empty 5-bucket
  // skeleton with `generated=false`).
  //
  // RC 1.51 (operator-locked, 2026-02) — the two-stage `unlocked`
  // gate has been REMOVED. The localStorage cache rehydrate path
  // is itself click-gated (the marker is written exclusively by
  // a successful paid Workspace click), so the presence of
  // `result.interview_prep` is proof of an active unlock for this
  // (user, job, source) tuple. No baseline auto-bind is possible.
  const interviewPrep = useMemo(
    () => mapInterviewPrep(result?.interview_prep || null),
    [result?.interview_prep],
  );
  const effectiveInterviewPrep = interviewPrep;

  const carryForwardLabel = useMemo(() => {
    if (sourceCvType === 'cv_variant') return 'tailored CV variant';
    return primaryCv?.original_filename || 'Source CV';
  }, [sourceCvType, primaryCv?.original_filename]);

  const runLabel = useMemo(
    // ── U-9.F — deterministic label flip (2026-02-15) ─────────────
    // "Regenerate Interview Prep" whenever a result is bound (from
    // this session OR from the mount-time discovery hook). Operator-
    // locked symmetric to U-9.D (CV) and the existing CL label.
    () => (result ? 'Regenerate Interview Prep' : 'Generate Interview Prep'),
    [result],
  );

  // operator #9 + #10 — Continue is DISABLED while running.
  // operator #11 — Continue ENABLED when not running even if no
  // result (skip allowed). The Pack section's own gating (per
  // RC 1.51 — "at least one generated artefact") guards the
  // downstream rule.
  const continueDisabled = running;

  // RC 1.49.7 Slice 5 URL-triple writer REMOVED (RC 1.51).
  // Interview Prep does NOT mirror its run handle into the URL —
  // PackSection now binds its `pack_snapshot.interview_prep`
  // payload directly from the same `interviewPrepUnlock`
  // localStorage marker this section writes on success. There is
  // no `analyse_fit_id` URL triple to keep in sync because the
  // Workspace deliverable is not a baseline analyse_fit row.

  return (
    <div
      data-testid={rootTestId}
      className="flex flex-col gap-5 min-w-0 max-w-full"
    >
      {/* ── Header with live pricing ─────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-ndor-ink">
            Interview Prep
          </h2>
          {/* U-9.G + U-9.C — Loaded-from-history chip + Checking
              status surface. */}
          {lookupStatus === 'checking' && (
            <span
              role="status"
              aria-live="polite"
              data-testid="workspace-fit-lookup-checking"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-ndor-ink-muted"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Checking for existing application materials…
            </span>
          )}
          {lookupStatus === 'ready' && loadedFromHistory && result?.interview_prep_id && (
            <HistoryBadge testId="workspace-fit-loaded-from-history-badge" />
          )}
          {/* RC 1.53 Phase 4.3A — activity chip. First visible, last
              gone — stays mounted through stabilization. */}
          <WorkspaceActivityIndicator
            jobId={jobId}
            actionCode="interview_prep"
            progressLabel={progressLabel}
          />
          {fitCostLabel != null && (
            <Badge
              className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0"
              data-testid="workspace-fit-price-label"
            >
              {fitCostLabel}
            </Badge>
          )}
        </div>
        <p className="text-xs text-ndor-ink-muted">
          Optional — Continue without one and skip to the Application Pack.
        </p>
      </div>

      {/* ── Source-CV carry-forward (read-only) ──────────────────── */}
      <div
        data-testid="workspace-fit-source-card"
        className="rounded-lg border border-ndor-line p-3 space-y-1"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Target className="h-4 w-4 text-ndor-ink-muted shrink-0" />
          <p className="text-sm font-medium text-ndor-ink truncate">
            Analysing {carryForwardLabel}
          </p>
        </div>
        <p className="text-[11px] text-ndor-ink-muted">
          Change this on the CV step if needed.
        </p>
      </div>

      {/* ── Restoring banner (mount-time localStorage rehydrate) ──
            RC 1.51 (operator-locked, 2026-02) — single synchronous
            localStorage read; banner appears + clears in one
            render so the result-card never lags. The legacy
            /best-fit recovery banner has been REMOVED — Workspace
            Interview Prep is blind to baseline analyse_fit
            hydration. ─────────────────────────────────────────── */}
      {restoring && !result && !running && (
        <div
          data-testid="workspace-fit-restoring-banner"
          className="rounded-lg border border-ndor-line bg-ndor-bg-subtle p-3 flex items-center gap-2"
        >
          <Loader2 className="h-4 w-4 animate-spin text-ndor-gold" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed">
            <p className="font-medium">Restoring your interview prep…</p>
            <p className="text-ndor-ink-muted">
              We&apos;re reading your saved interview prep — no Credits charged.
            </p>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 H2 — inline inferior-source confirmation ──
           Shown ONLY when the backend's R2/R3 reject-gate fires.
           Calm non-floating panel. Two inline controls. No
           automatic retry — the user must explicitly click
           Analyse anyway. force_override: true is sent ONLY from
           this path. */}
      {inferiorGuard && (
        <div
          data-testid="workspace-fit-inferior-guard"
          role="alert"
          className="rounded-lg border-2 border-ndor-warning/40 bg-ndor-warning/[0.05] p-3 space-y-3"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-ndor-warning shrink-0 mt-0.5" aria-hidden />
            <div className="text-xs text-ndor-ink leading-relaxed space-y-1">
              <p
                className="font-medium text-sm"
                data-testid="workspace-fit-inferior-guard-title"
              >
                {inferiorGuard.code === 'INFERIOR_VARIANT'
                  ? 'This tailored variant looks weaker than your Source CV.'
                  : 'This CV looks weaker than your current baseline.'}
              </p>
              <p
                className="text-ndor-ink-muted"
                data-testid="workspace-fit-inferior-guard-copy"
              >
                Running Interview Prep on it would normally cost you Credits without
                improving how competitive you appear for this role. You can
                keep your existing analysis, or proceed anyway.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setInferiorGuard(null)}
              disabled={!!inferiorGuard.busy}
              data-testid="workspace-fit-inferior-guard-cancel"
            >
              Keep existing analysis
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleRun({ force: true, userInitiated: true })}
              disabled={!!inferiorGuard.busy || creditsLoading || !sourceCvId}
              data-testid="workspace-fit-inferior-guard-force"
            >
              {inferiorGuard.busy
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Analyse anyway
            </Button>
          </div>
        </div>
      )}

      {/* ── Result card (when present) ──────────────────────────── */}
      {/* RC 1.50 R.3 — Interview Prep render replaces the legacy
            score-card surface. The numeric Fit/CV score lines are
            intentionally hidden inside the workspace per operator
            spec: the baseline diagnostic page on the job context
            still shows them. The workspace surface now leads with
            the operator's 5 interview-prep buckets. Billing and
            refund lines preserved (financial transparency). ─── */}

      {/* ── U-3.IP — Regenerate confirm dialog ──────────────────── */}
      {regenerateConfirm && (
        <div
          className="my-4 rounded-lg border border-amber-500/60 bg-amber-500/10 p-4 text-sm"
          data-testid="ip-regenerate-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ip-regenerate-confirm-title"
        >
          <div
            id="ip-regenerate-confirm-title"
            className="font-medium text-amber-100 mb-1.5"
          >
            Regenerate Interview Prep?
          </div>
          <div className="text-amber-50/80 mb-3">
            You already have an Interview Prep for this job. Generating a new one
            will charge another 15 credits and replace the version shown above.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRegenerateConfirm(null)}
              data-testid="ip-regenerate-cancel"
            >
              Keep existing
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setRegenerateConfirm(null);
                handleRun({ force: true, userInitiated: true });
              }}
              data-testid="ip-regenerate-confirm-go"
              disabled={running || creditsLoading || !sourceCvId}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Generate new (15 credits)
            </Button>
          </div>
        </div>
      )}
      {result && (
        <div
          data-testid="workspace-fit-result-card"
          data-cv-context-source-type={sourceCvType}
          data-cv-context-source-id={sourceCvId || ''}
          data-row-cv-variant-id={result?.linked_cv_variant_id || ''}
          data-row-base-cv-id={result?.linked_base_cv_id || ''}
          className="rounded-lg border-2 border-ndor-gold/40 bg-ndor-gold/[0.02] p-3 space-y-2"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-ndor-gold shrink-0" />
            <p
              className="text-sm font-medium text-ndor-ink"
              data-testid={effectiveInterviewPrep.generated
                ? 'workspace-fit-prep-ready-header'
                : 'workspace-fit-prep-not-ready-header'}
            >
              {effectiveInterviewPrep.generated
                ? 'Interview prep ready'
                : 'Interview Prep — not yet generated for this run'}
            </p>
            {effectiveInterviewPrep.generated && (
              <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0">
                {wasRecovered
                  ? 'Recovered · just now'
                  : cached
                    ? 'Existing analysis · no charge'
                    : 'Fresh run · this attempt'}
              </Badge>
            )}
            {effectiveInterviewPrep.generated && (
              <CheckCircle2 className="h-3.5 w-3.5 text-ndor-success ml-auto" aria-hidden />
            )}
          </div>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed flex items-center gap-1.5">
            <Info className="h-3 w-3 shrink-0" aria-hidden />
            {effectiveInterviewPrep.generated
              ? (wasRecovered
                ? 'Restored from your last Run attempt — no new Credits charged.'
                : cached
                  ? 'Loaded from your prior run for this CV — no Credits charged.'
                  : 'Saved to your job history.')
              : 'Click Run Interview Prep below to generate the 5-bucket prep for this CV.'}
          </p>

          {/* ── RC 1.49.7 Slice 3 — Inline billing summary
                (operator P0, 2026-02). Render-only — fields read
                directly from the wire `result.billing`. Cached /
                recovered paths stay zero by contract. Fresh run
                surfaces backend `usage_consumed`. Free-fit-used
                badge surfaces when backend exposes it. Per
                billing_contract.md §6.1. ───────────────────────── */}
          {(result?.billing?.usage_consumed != null
            || result?.billing?.free_fit_used != null) && (
            <p
              className={
                'text-[11px] '
                + ((result?.billing?.usage_consumed != null
                    && result.billing.usage_consumed > 0)
                  ? 'text-ndor-warning'
                  : 'text-ndor-ink-muted')
              }
              data-testid="workspace-fit-billing-line"
            >
              {result?.billing?.usage_consumed != null && (
                <span>
                  Charged{' '}
                  <span
                    className="font-mono-ui tabular-nums"
                    data-testid="workspace-fit-billing-usage-consumed"
                  >
                    {result.billing.usage_consumed}
                  </span>
                  {' '}credit{result.billing.usage_consumed === 1 ? '' : 's'}
                </span>
              )}
              {result?.billing?.free_fit_used && (
                <span
                  className="ml-1"
                  data-testid="workspace-fit-billing-free-fit-used"
                >
                  · Used your free analysis allowance
                </span>
              )}
            </p>
          )}

          {/* ── RC 1.49.7 Slice 5d-A — Inline refund transparency
                (operator P0, 2026-02). Backend RC 1.44.H4 auto-refunds
                a variant Analyse Fit charge whenever the variant's
                fit_score is strictly below the user's existing
                Source-CV baseline for the same job. The refund
                happens silently in the wallet (`refund_usage`); pre-
                Slice-5d-A the workspace had NO user-facing surface
                for it because:
                  (a) the cv_variant `pack_snapshot.fit` path doesn't
                      hit `FitResultCard` (which reads `reasoning_json
                      .refunded` from `job_scores` — a row that does
                      not exist for transient variants per Stage 2);
                  (b) the inline billing line above only renders
                      `usage_consumed` (which the route correctly
                      zeros after refund), so a refunded run looked
                      indistinguishable from a free-tier run.
                The wire DOES carry `billing.refunded: true` and a
                machine-readable `refund_reason` (e.g.
                "INFERIOR_FRESH_VARIANT"). We surface BOTH inline,
                non-floating, no-toast. Render-only — zero billing
                math on the client (operator's hard rail). ───────── */}
          {result?.billing?.refunded && (
            <p
              className="text-[11px] text-ndor-success leading-relaxed flex items-start gap-1.5"
              data-testid="workspace-fit-refund-line"
            >
              <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
              <span>
                <span className="font-medium">Credits refunded.</span>{' '}
                {result.billing.refund_reason === 'INFERIOR_FRESH_VARIANT'
                  ? 'Credits returned to your wallet automatically.'
                  : 'Credits returned to your wallet automatically.'}
                {result.billing.refund_reason && (
                  <span
                    className="ml-1 text-ndor-ink-muted"
                    data-testid="workspace-fit-refund-reason"
                  >
                    (reason: <code className="font-mono-ui">{result.billing.refund_reason}</code>)
                  </span>
                )}
              </span>
            </p>
          )}

          {/* ── RC 1.50 R.3 — 5-bucket Interview Prep render. Read-
                only over `result.interview_prep`. No new chargeable
                surface. Slice 3A (2026-05-11) — the render block is
                now owned by the reusable `InterviewPrepViewer`
                component (also consumed by the Documents library).
                Visual / data-testid output is byte-identical. ─ */}
          <InterviewPrepViewer interviewPrep={result?.interview_prep} />

          {/* RC 1.55 P0 — post-terminal hydration-pending banner.
              Surfaced ONLY when the retry schedule on
              `GET /api/interview-prep/{id}` has exhausted. The
              artefact is saved + charged; Retry button re-invokes
              the canonical READ-ONLY GET — never `/api/runs`,
              never a new charge. */}
          {pendingHydration && (
            <div
              data-testid="workspace-ip-hydration-pending"
              className="flex items-center gap-2 rounded-md border border-ndor-info-border bg-ndor-info-light/60 px-3 py-2 mt-3"
            >
              <Loader2 className="h-3.5 w-3.5 text-ndor-info shrink-0 animate-spin" />
              <p className="text-[11px] text-ndor-ink-secondary flex-1 leading-relaxed">
                Generated and saved. Refreshing preview…
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-[11px]"
                onClick={handleRetryHydration}
                disabled={retryingHydration}
                data-testid="workspace-ip-hydration-retry-button"
              >
                {retryingHydration
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <RefreshCw className="h-3 w-3 mr-1" />}
                Refresh preview
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── RC 1.50 polish — Three-control footer (Back · Run ·
            Continue). Operator UX direction 2026-02-09: footer
            structure matches CV section (Back left · primary
            middle · Continue right). Back routes to the previous
            workspace section (Cover Letter). Sticky bottom and
            busy-disable behaviour preserved. ─────────────────── */}
      <footer
        data-testid="workspace-section-footer"
        /* RC 1.55 P0 Mobile pass — stack on <xs, wrap from xs+. */
        className="sticky bottom-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex flex-col xs:flex-row xs:flex-wrap xs:items-center xs:justify-between gap-2 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
          data-testid="workspace-section-back-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          <ArrowLeft className="mr-1 sm:mr-1.5 h-3.5 w-3.5" />
          <span className="hidden sm:inline">Back to Cover Letter</span>
          <span className="sm:hidden">Back</span>
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={() => {
            // U-3.IP — Regenerate confirm gate. If a terminal IP is
            // already bound, intercept the dispatch and ask the user
            // to confirm the new spend. First-time generation
            // (no result bound) proceeds directly.
            if (result?.interview_prep_id) {
              setRegenerateConfirm({ force: false });
              return;
            }
            handleRun({ userInitiated: true });
          }}
          /* RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
             Phase 4.3A — also lock on the controller's active states. */
          disabled={
            running
            || creditsLoading
            || !sourceCvId
            || interviewPrepCooldown.active
            || workspaceBusy
            || controllerStatus === 'dispatching'
            || controllerStatus === 'polling'
            || controllerStatus === 'stabilizing'
            /* U-9.B — lock Generate while mount-time existing-artefact
               discovery is in flight. Closes the pre-U-9 race window. */
            || lookupStatus !== 'ready'
          }
          data-controller-status={controllerStatus}
          data-testid="workspace-fit-analyse-button"
          data-cooldown-active={interviewPrepCooldown.active ? '1' : '0'}
          data-cooldown-remaining-ms={interviewPrepCooldown.active ? String(interviewPrepCooldown.remainingMs) : ''}
          title={interviewPrepCooldown.active
            ? `Generate is on cooldown for ${interviewPrepCooldown.label}. This prevents accidental re-clicks while your wallet syncs.`
            : undefined}
          /* RC 1.55 P0 Mobile pass — grow on narrow widths. */
          className="w-full xs:w-auto xs:flex-1 xs:min-w-0 sm:flex-none px-2 sm:px-3"
        >
          {running
            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin shrink-0" />
            : <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
          {(() => {
            const full = interviewPrepCooldown.active
              ? `Run again in ${interviewPrepCooldown.label}`
              : runLabel;
            const compact = interviewPrepCooldown.active
              ? 'Wait'
              : (typeof runLabel === 'string' && runLabel.startsWith('Re') ? 'Regen' : 'Generate');
            return (
              <>
                <span className="hidden xs:inline truncate" aria-hidden="true">{full}</span>
                <span className="xs:hidden truncate" aria-hidden="true">{compact}</span>
                <span className="sr-only">{full}</span>
              </>
            );
          })()}
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={onContinue}
          disabled={continueDisabled}
          data-testid="workspace-section-continue-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          Continue
          <ArrowRight className="ml-1 sm:ml-1.5 h-3.5 w-3.5" />
        </Button>
      </footer>

      {creditsLoading && !running && (
        <p
          className="text-[11px] text-ndor-ink-muted"
          data-testid="workspace-fit-credits-loading-hint"
        >
          Loading your wallet…
        </p>
      )}
    </div>
  );
}
